/* eslint-disable no-unsafe-optional-chaining */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveSettingsTab } from 'reducers/uiSlice';
import axios from 'axios';
import { TabContent, TabPane } from 'reactstrap';
import CustomTabs from 'components/CustomTabs';
import Loader from 'components/common/Loader';
import { modifiedUnderScore } from 'utils/constants';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { authContext } from 'contexts/AuthContext';
import Heading from 'components/Heading';
import GeneralSetting from './components/General';
import { APP_SETTINGS } from './SettingsGraphQL';
import IndianSellerCommission from './components/IndianSellerCommission';
import IndianBuyerCommission from './components/IndianBuyerCommission';
import TaskManagementSetting from '../Task/components/AutomatedTaskSettings';
import FlagUI from './components/FlagUI';
import CityMaster from './components/CityMaster';
import { SETTINGS_TABS_LIST } from './constants';

const Settings = ({ drawerToggle }) => {
  const { auth } = useContext(authContext);
  const dispatch = useDispatch();
  // Get active tab from Redux instead of local state
  const activeTab = useSelector((state) => state.ui.activeSettingsTab);
  const [indianSellerCommissionData, setIndianSellerCommissionData] =
    useState(null);
  const [loadingCommissionData, setLoadingCommissionData] = useState(false);

  const { data, loading, error } = useQuery(APP_SETTINGS, {
    variables: {
      filter: [],
      pageNo: 0,
      pageSize: 10,
      sortCol: null,
      sortDirection: null,
      pagination: false,
    },
    fetchPolicy: 'network-only',
  });

  const toggle = (tab) => {
    if (activeTab !== tab) dispatch(setActiveSettingsTab(tab));
  };

  const fetchIndianSellerCommissionData = useCallback(() => {
    setLoadingCommissionData(true);
    axios
      .get(
        `${process.env.REACT_APP_REST_API_ENDPOINT}commission/fetch-all-commission-slabs`,
        {
          headers: {
            Authorization: `Bearer ${auth?.data?.token}`, // Assuming token is stored in localStorage
          },
        },
      )
      .then((response) => {
        setIndianSellerCommissionData(response.data);
        setLoadingCommissionData(false);
      })
      .catch((err) => {
        console.error('Error fetching Indian Seller Commission data:', err);
        setLoadingCommissionData(false);
      });
  }, [auth?.data?.token]);

  useEffect(() => {
    // Set default tab only if current active tab is not valid
    if (
      data?.appSettings?.appSettings?.length &&
      (!activeTab ||
        !data.appSettings.appSettings.some(
          (setting) =>
            modifiedUnderScore(setting.type) === activeTab ||
            activeTab === SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION ||
            activeTab === SETTINGS_TABS_LIST.INDIAN_BUYER_COMMISSION ||
            activeTab === SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING ||
            activeTab === SETTINGS_TABS_LIST.FLAG_UI ||
            activeTab === SETTINGS_TABS_LIST.CITY_MASTER,
        ))
    ) {
      dispatch(
        setActiveSettingsTab(
          modifiedUnderScore(data.appSettings.appSettings[0].type),
        ),
      );
    }
    if (activeTab === SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION) {
      fetchIndianSellerCommissionData();
    }
  }, [data?.appSettings?.appSettings, activeTab, dispatch, fetchIndianSellerCommissionData]);

  if (loading) return <Loader />;
  if (error) return `There is an error on this page : ${error.message} `;
  if (
    activeTab !== SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION &&
    activeTab !== SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING &&
    activeTab !== SETTINGS_TABS_LIST.FLAG_UI &&
    activeTab !== SETTINGS_TABS_LIST.CITY_MASTER &&
    !data?.appSettings?.appSettings
  )
    return 'Data not available';

  // Determine which tabs to show
  const tabsToShow =
    data?.appSettings?.appSettings?.map((value) =>
      modifiedUnderScore(value.type),
    ) || [];

  if (!tabsToShow.includes(SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION)) {
    tabsToShow.push(SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION);
  }

  if (!tabsToShow.includes(SETTINGS_TABS_LIST.INDIAN_BUYER_COMMISSION)) {
    tabsToShow.push(SETTINGS_TABS_LIST.INDIAN_BUYER_COMMISSION);
  }

  if (!tabsToShow.includes(SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING)) {
    tabsToShow.push(SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING);
  }

  if (!tabsToShow.includes(SETTINGS_TABS_LIST.FLAG_UI)) {
    tabsToShow.push(SETTINGS_TABS_LIST.FLAG_UI);
  }

  if (!tabsToShow.includes(SETTINGS_TABS_LIST.CITY_MASTER)) {
    tabsToShow.push(SETTINGS_TABS_LIST.CITY_MASTER);
  }

  return (
    <div className="application-page">
      <div className="top-head">
        <Heading title="Settings" />
      </div>

      <div className="tab-sec">
        <CustomTabs
          tabs={tabsToShow}
          selectedTab={activeTab}
          onTabChange={toggle}
          className="outer-tab w-auto"
        />
        <TabContent activeTab={activeTab} className="w-100 px-5 mx-md-4 settings-tab-content">
          {/* Conditional rendering based on active tab */}
          {activeTab === SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION && (
            <TabPane tabId="INDIAN SELLER COMMISSION">
              <ErrorBoundary>
                {loadingCommissionData && <Loader />}
                {!loadingCommissionData && (
                  <div className="indian-seller-commission-container">
                    {indianSellerCommissionData && (
                      <IndianSellerCommission
                        fetchIndianSellerCommissionData={
                          fetchIndianSellerCommissionData
                        }
                        commissionData={indianSellerCommissionData?.data}
                      />
                    )}
                    {!indianSellerCommissionData && (
                      <div>No commission data available</div>
                    )}
                  </div>
                )}
              </ErrorBoundary>
            </TabPane>
          )}

          {activeTab === SETTINGS_TABS_LIST.INDIAN_BUYER_COMMISSION && (
            <TabPane tabId="INDIAN BUYER COMMISSION">
              <ErrorBoundary>
                <div className="indian-seller-commission-container">
                  <IndianBuyerCommission />
                </div>
              </ErrorBoundary>
            </TabPane>
          )}

          {activeTab === SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING && (
            <TabPane tabId={SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING}>
              <ErrorBoundary>
                <TaskManagementSetting />
              </ErrorBoundary>
            </TabPane>
          )}

          {activeTab === SETTINGS_TABS_LIST.FLAG_UI && (
            <TabPane tabId={SETTINGS_TABS_LIST.FLAG_UI}>
              <ErrorBoundary>
                <FlagUI />
              </ErrorBoundary>
            </TabPane>
          )}

          {activeTab === SETTINGS_TABS_LIST.CITY_MASTER && (
            <TabPane tabId={SETTINGS_TABS_LIST.CITY_MASTER}>
              <ErrorBoundary>
                <CityMaster />
              </ErrorBoundary>
            </TabPane>
          )}

          {activeTab !== SETTINGS_TABS_LIST.INDIAN_SELLER_COMMISSION &&
            activeTab !== SETTINGS_TABS_LIST.INDIAN_BUYER_COMMISSION &&
            activeTab !== SETTINGS_TABS_LIST.TASK_MANAGEMENT_SETTING &&
            activeTab !== SETTINGS_TABS_LIST.FLAG_UI &&
            activeTab !== SETTINGS_TABS_LIST.CITY_MASTER &&
            data?.appSettings?.appSettings?.map((value) => {
              const tabId = modifiedUnderScore(value.type);
              return (
                <TabPane tabId={tabId} key={value.type}>
                  {activeTab === tabId && (
                    <ErrorBoundary>
                      <GeneralSetting
                        type={value.type}
                        drawerToggle={drawerToggle}
                      />
                    </ErrorBoundary>
                  )}
                </TabPane>
              );
            })}
        </TabContent>
      </div>
    </div>
  );
};

export default Settings;
